import {
  createSlice,
  type SerializedError,
  type PayloadAction,
  type WithSlice,
} from '@reduxjs/toolkit';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { rootReducer } from '@verantos-dev/app-state-base';
import {
  type InputFieldConfig,
  type CohortBuilderDefinitionLegacy,
} from '@verantos-dev/types-cohorts';

export interface CohortDefinitionState {
  /** Whether the cohort profile is loading */
  isLoading: boolean;

  /** Whether the cohort version (the dsl) is loading */
  isVersionLoading: boolean;

  /** Error state for the cohort definition */
  error: FetchBaseQueryError | SerializedError | undefined;

  /** Error state for the cohort version */
  versionError: FetchBaseQueryError | SerializedError | undefined;

  /** The staged cohort definition after translation from the backend or the research assistant */
  cohortDefinitionLegacy: CohortBuilderDefinitionLegacy | null;

  /** The committed (last saved) definition - used for frontend-side dirty detection */
  committedDefinition: CohortBuilderDefinitionLegacy | null;

  /** Whether the cohort definition is editable by the user */
  isEditable: boolean;

  /** Whether the data has been staged for review by the research assistant */
  isStagedFromResearchAssistant: boolean;

  /** Whether the data has been staged for review by the user */
  isStagedFromUserEdit: boolean;

  /** Input field configuration map for the staged cohort definition */
  inputFieldConfigMap: Record<string, InputFieldConfig> | undefined;

  /** Last sync time for the staged cohort definition */
  lastSyncTime: string | null;

  /** Whether auto-save is currently in progress */
  isSaving: boolean;
}

export enum CohortDefinitionStagingSource {
  RESEARCH_ASSISTANT = 'RESEARCH_ASSISTANT',
  BACKEND = 'BACKEND',
  USER_EDIT = 'USER_EDIT',
}

const initialState: CohortDefinitionState = {
  isLoading: true,
  isVersionLoading: true,
  error: undefined,
  versionError: undefined,
  cohortDefinitionLegacy: null,
  committedDefinition: null,
  isEditable: false,
  isStagedFromResearchAssistant: false,
  isStagedFromUserEdit: false,
  lastSyncTime: null,
  inputFieldConfigMap: undefined,
  isSaving: false,
};

const cohortDefinitionSlice = createSlice({
  name: 'cohortDefinitionStaging',
  initialState,
  reducers: {
    setCohortDefinitionTransitionStates: (
      state,
      action: PayloadAction<{
        isLoading: boolean;
        error: SerializedError | FetchBaseQueryError | undefined;
      }>
    ) => {
      state.isLoading = action.payload.isLoading;
      state.error = action.payload.error;
    },
    setCohortDefinitionVersionTransitionStates: (
      state,
      action: PayloadAction<{
        isLoading: boolean;
        error: SerializedError | FetchBaseQueryError | undefined;
      }>
    ) => {
      state.isVersionLoading = action.payload.isLoading;
      state.versionError = action.payload.error;
    },
    setCohortDefinitionLegacy: (
      state,
      action: PayloadAction<{
        cohortDefinitionLegacy: CohortBuilderDefinitionLegacy;
        isStagedFromResearchAssistant: boolean;
        isEditable?: boolean;
      }>
    ) => {
      state.cohortDefinitionLegacy = action.payload.cohortDefinitionLegacy;
      state.isEditable = action.payload.isEditable ?? state.isEditable;
      state.isStagedFromResearchAssistant = action.payload.isStagedFromResearchAssistant;
      state.lastSyncTime = new Date().toISOString();
    },
    resetCohortDefinitionLegacy: (state) => {
      return {
        ...initialState,
      };
    },
    clearStagedFromResearchAssistant: (state) => {
      state.isStagedFromResearchAssistant = false;
    },
    setIsSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setHasLocalUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.isStagedFromUserEdit = action.payload;
    },
    /** Set committed definition when loading from backend or after explicit Save */
    setCommittedDefinition: (
      state,
      action: PayloadAction<CohortBuilderDefinitionLegacy | null>
    ) => {
      state.committedDefinition = action.payload;
    },
    /** Clear local unsaved changes (called when backend confirms or user reverts) */
    clearLocalUnsavedChanges: (state) => {
      state.isStagedFromUserEdit = false;
    },
    /** Set whether the current user can edit the cohort (owner check). */
    setIsEditable: (state, action: PayloadAction<boolean>) => {
      state.isEditable = action.payload;
    },
  },
});

export const {
  setCohortDefinitionTransitionStates,
  setCohortDefinitionVersionTransitionStates,
  setCohortDefinitionLegacy,
  resetCohortDefinitionLegacy,
  clearStagedFromResearchAssistant,
  setIsSaving,
  setHasLocalUnsavedChanges,
  setCommittedDefinition,
  clearLocalUnsavedChanges,
  setIsEditable,
} = cohortDefinitionSlice.actions;

declare module '@verantos-dev/app-state-base' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof cohortDefinitionSlice> {}
}

// Inject the slice into the root reducer
cohortDefinitionSlice.injectInto(rootReducer);
